import {useCallback} from 'react'
import {ScrollView, View} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useFocusEffect} from '@react-navigation/native'
import {Image} from 'expo-image'
import {
  type CommonNavigatorParams,
  type NativeStackScreenProps,
} from '#/lib/routes/types'
import {useSetMinimalShellMode} from '#/state/shell'
import * as Layout from '#/components/Layout'
import {ButtonText} from '#/components/Button'
import {Link} from '#/components/Link'
import {useAnalytics} from '#/analytics'
import {atoms as a, useTheme} from '#/alf'
import {Text} from '#/components/Typography'
import {getPosterUrl} from '#/lib/tmdb'
import {VideoPlayer} from '#/components/VideoPlayer'

type Props = NativeStackScreenProps<CommonNavigatorParams, 'Video'>

export function VideoScreen({route}: Props) {
  const setMinimalShellMode = useSetMinimalShellMode()
  const ax = useAnalytics()
  const t = useTheme()
  const {_} = useLingui()
  const params = route.params

  useFocusEffect(
    useCallback(() => {
      setMinimalShellMode(false)
      ax.metric('video:view', {})
    }, [setMinimalShellMode, ax]),
  )

  if (!params || !params.id) {
    return (
      <Layout.Screen testID="videoScreen">
        <Layout.Header.Outer>
          <Layout.Header.BackButton />
          <Layout.Header.Content>
            <Layout.Header.TitleText>
              <Trans>Video</Trans>
            </Layout.Header.TitleText>
          </Layout.Header.Content>
          <Layout.Header.Slot />
        </Layout.Header.Outer>
        <View
          style={[
            a.flex_1,
            a.align_center,
            a.justify_center,
            a.gap_lg,
            a.p_lg,
          ]}>
          <Text
            style={[a.text_lg, a.text_center, t.atoms.text_contrast_medium]}>
            <Trans>
              Open a SHXRE video link (e.g. shxre.net/video/…) or browse videos
              below.
            </Trans>
          </Text>
          <Link
            to="/feeds/videos"
            action="navigate"
            label={_(msg`Browse SHXRE videos`)}
            size="small"
            color="primary">
            <ButtonText>
              <Trans>Browse SHXRE videos</Trans>
            </ButtonText>
          </Link>
        </View>
      </Layout.Screen>
    )
  }

  const posterUrl = getPosterUrl(params.poster_path)
  const releaseYear = params.release_date
    ? new Date(params.release_date).getFullYear()
    : null
  const mediaType = params.media_type === 'tv' ? 'TV Show' : 'Movie'

  return (
    <Layout.Screen testID="videoScreen">
      <Layout.Header.Outer>
        <Layout.Header.BackButton />
        <Layout.Header.Content>
          <Layout.Header.TitleText>
            {params.title}
          </Layout.Header.TitleText>
        </Layout.Header.Content>
        <Layout.Header.Slot />
      </Layout.Header.Outer>
      <ScrollView
        style={[a.flex_1, t.atoms.bg]}
        contentContainerStyle={[a.px_lg, a.py_lg]}>
        <View
          style={[
            a.gap_lg,
            {
              maxWidth: 600,
              alignSelf: 'center',
              width: '100%',
            },
          ]}>
          <View
            style={[
              a.flex_row,
              a.gap_lg,
            ]}>
            {/* Poster */}
            {posterUrl && (
              <View
                style={[
                  {
                    width: 200,
                    height: 300,
                    borderRadius: 12,
                    overflow: 'hidden',
                    backgroundColor: t.palette.contrast_50,
                  },
                ]}>
                <Image
                  source={{uri: posterUrl}}
                  style={{width: '100%', height: '100%'}}
                  contentFit="cover"
                  transition={200}
                />
              </View>
            )}

            {/* Media Info */}
            <View style={[a.flex_1, a.gap_md]}>
              <View style={[a.flex_row, a.align_center, a.gap_md, a.flex_wrap]}>
                <Text style={[a.text_3xl, a.font_bold, a.leading_tight]}>
                  {params.title}
                </Text>
                <Text
                  style={[
                    a.text_sm,
                    a.font_medium,
                    t.atoms.text_contrast_medium,
                    {
                      backgroundColor: t.palette.contrast_100,
                      paddingHorizontal: 8,
                      paddingVertical: 4,
                      borderRadius: 6,
                    },
                  ]}>
                  {mediaType}
                </Text>
              </View>

              <View style={[a.flex_row, a.align_center, a.gap_lg, a.flex_wrap]}>
                {releaseYear && (
                  <Text
                    style={[a.text_lg, t.atoms.text_contrast_high, a.font_semi_bold]}>
                    {releaseYear}
                  </Text>
                )}
                <View style={[a.flex_row, a.align_center, a.gap_xs]}>
                  <Text
                    style={[a.text_lg, a.font_bold, t.atoms.text_contrast_high]}>
                    ⭐ {typeof params.vote_average === 'number' ? params.vote_average.toFixed(1) : 'N/A'}
                  </Text>
                </View>
              </View>

              <View style={[a.pt_md]}>
                <Text
                  style={[
                    a.text_base,
                    t.atoms.text_contrast_high,
                    a.leading_normal,
                  ]}>
                  {params.overview || 'No overview available.'}
                </Text>
              </View>

              <View style={[a.pt_sm]}>
                <Text
                  style={[
                    a.text_sm,
                    t.atoms.text_contrast_medium,
                  ]}>
                  <Trans>TMDB ID: {params.id}</Trans>
                </Text>
              </View>
            </View>
          </View>

          {/* Video Player */}
          <View style={[a.pt_xl]}>
            <VideoPlayer
              tmdbId={params.id}
              contentType={params.media_type}
            serverList={[
              {serverurl: 'https://vidlink.pro/movie/'},
              {serverurl: 'https://vidlink.pro/tv/'},
              {serverurl: 'https://vidsrc-embed.ru/embed/movie/'},
              {serverurl: 'https://vidsrc-embed.ru/embed/tv/'},
              {serverurl: 'https://vidsrcme.ru/embed/movie'},
              {serverurl: 'https://vidsrcme.ru/embed/tv'},
            ]}
              wt={params.wt}
            />
          </View>
        </View>
      </ScrollView>
    </Layout.Screen>
  )
}
